// @flow
import BaseCollection from "../base/BaseCollection";

import StringFilterModel from "../filters/StringFilterModel";
import RangeFilterModel from "../filters/RangeFilterModel";
import AssignmentFilterModel from "../filters/AssignmentFilterModel";
import ConceptIndexFilterModel from "../filters/ConceptIndexFilterModel";
import CompositeAttributeModel from "../attributes/CompositeAttributeModel";
import FilterModel from "../filters/FilterModel";

import type { AttributeType, IFilter, ModelOptions } from "../types";
import { PARAMETER_TYPES } from "../../constants";

/**
 * Contains a collection of filters
 */
export default class FilterCollection extends BaseCollection<IFilter> {
  /**
   * Construct a collection of filters
   */
  constructor(
    data: Object = {},
    contributions: Object = {},
    modelOptions?: ModelOptions,
  ) {
    super();

    if (data && contributions && Array.isArray(contributions.filter)) {
      this.collection = contributions.filter
        .filter((filterContribution) => Object.keys(filterContribution)[0])
        .map((filterContribution) => {
          const [filterKey] = Object.keys(filterContribution);
          const filterData = data[filterKey];
          const filterContributionByKey = filterContribution[filterKey];

          const filter = this.createFilter(
            filterKey,
            {
              ...filterData,
              dynamicschema: contributions.dynamicschema,
            },
            filterContributionByKey,
            modelOptions,
          );

          if (contributions.listkey) {
            filter.listkey = contributions.listkey;
          }

          if (contributions.contexts && filterContributionByKey.contextid) {
            const filterContext = contributions.contexts.find(
              (context) => context.id === filterContributionByKey.contextid,
            );
            if (filterContext) {
              filter.context = filterContext;
            }
          }

          return filter;
        });
    }
  }

  /**
   */
  createFilter(
    filterKey: string,
    data: Object,
    contributions: Object,
    modelOptions?: ModelOptions,
  ): IFilter {
    const type = contributions.type || "stringfilter";
    if (type === "choicefilter" && filterKey === "index") {
      return new ConceptIndexFilterModel(data, contributions, modelOptions);
    }

    if (type.includes("rangefilter")) {
      return new RangeFilterModel(data, contributions, modelOptions);
    }

    if (type === "assignmentfilter") {
      return new AssignmentFilterModel(data, contributions, modelOptions);
    }

    if (type === "stringfilter") {
      return new StringFilterModel(data, contributions, modelOptions);
    }

    return new FilterModel(data, contributions, modelOptions);
  }

  /**
   * Call the reset function on all filters
   *
   * @see {IFilter#reset()}
   */
  reset(): FilterCollection {
    this.collection = this.collection.map((filter) => filter.reset());

    return this;
  }

  /**
   * Checks if range attribute key equals key
   */
  checkRangeFilterByAttributeKey(
    filter: RangeFilterModel,
    key: string,
  ): boolean {
    const rangeAttribute = filter.attribute;

    if (rangeAttribute instanceof CompositeAttributeModel) {
      return (
        rangeAttribute.key === key ||
        (rangeAttribute.start && rangeAttribute.start.key === key) ||
        (rangeAttribute.end && rangeAttribute.end.key === key)
      );
    }

    return false;
  }

  /**
   * Check if assignment filter attribute matches key
   */
  checkAssignmentFilterByAttributeKey(
    filter: AssignmentFilterModel,
    key: string,
  ): boolean {
    return (
      filter.key === key ||
      filter.user?.key === key ||
      filter.assignmenttype?.key === key
    );
  }

  /**
   */
  getFilterByName(name: string): IFilter | null {
    return this.find((filter) => filter.name === name) || null;
  }

  /**
   * Getting the filter by name
   */
  getFilterByAttributeKey(key: string): IFilter | null {
    return (
      this.find((filter) => {
        if (filter instanceof RangeFilterModel) {
          return this.checkRangeFilterByAttributeKey(filter, key);
        }

        if (filter instanceof AssignmentFilterModel) {
          return this.checkAssignmentFilterByAttributeKey(filter, key);
        }

        if (filter instanceof FilterModel) {
          return filter.attribute.key === key;
        }

        return false;
      }) || null
    );
  }

  /**
   * Update Filter by input name and value
   */
  update(attribute: AttributeType, value: string): void {
    const filterToUpdate = attribute.parentKey
      ? this.getFilterByAttributeKey(attribute.parentKey)
      : this.getFilterByAttributeKey(attribute.key);

    if (!filterToUpdate) {
      throw new Error(`Can not find filter by attribute key: ${attribute.key}`);
    }

    const newFilter = filterToUpdate.clone();
    newFilter.update(attribute, value);

    this.collection = this.collection.map((filter) => {
      if (filter.name === newFilter.name) {
        return newFilter;
      }

      return filter;
    });
  }

  /**
   * Retrieve if all filters are valid
   */
  get isValid(): boolean {
    return this.collection.every((filter) => filter.isValid);
  }

  /**
   * Indicates if an active filter is present in the collection
   */
  hasActiveFilters(): boolean {
    return this.collection.some((filter) => filter.isActive());
  }

  /**
   * Retrieve all filters with a value
   */
  getActiveFilters(): Array<IFilter> {
    return this.collection.filter((filter) => filter.isActive());
  }

  /**
   */
  get formdata(): { [string]: any } | null {
    const bodyFields = this.filter(
      (f) =>
        f.parameterType === PARAMETER_TYPES.BODY && f.hasValue() && f.formdata,
    );

    if (bodyFields.length === 0) {
      return null;
    }

    return bodyFields.reduce((data, f) => {
      return Object.assign(data, f.formdata);
    }, {});
  }
}
